/**
 * mockHost — the studio's stand-in for the app's widget host bridge.
 *
 * Every slot in the registry's `host` component maps 1:1 to a real app
 * callback (`onOpenItem`, `onOpenCapture`, `onOpenSession`, `onPickMedia`, …).
 * In the studio there is no app, so each slot records what WOULD have happened
 * and returns the shape the real bridge returns — that is what lets the
 * preview exercise the whole connection graph, including the chains that fire
 * on a host RESULT (pick media → background capture).
 *
 * `push` receives `{ op, payload, at }`; the studio renders them as a trace.
 *
 * THE INBOUND HALF. `connect(emit)` is how a bridge talks BACK: the runtime
 * hands it an emitter and everything the mock sends through it arrives as a
 * `host.<signal>` a blueprint can wire. Today those signals include
 * `navActionTriggered` for host chrome and `captureResult` for the terminal
 * Session-dispatch answer; neither has an in-page control that can substitute
 * for the host callback.
 */

export function createMockHost({
  push, navigatePage, onNavAction, mediaSamples = [], itemPage = null,
  // A slot that OPENS A SESSION (openCapture / openMainSession / openSession /
  // a delegated openPage, or a closePage carrying a RESULT) is handed to this
  // so the studio can REALLY start it, exactly like the app — not just record
  // the request. Absent → the slot is traced only, as before.
  onSessionOpen = null,
  // Which page ids this bundle renders NATIVELY. openPage to one of these is a
  // page transition; openPage to anything else is a DELEGATED WebView page the
  // native studio cannot run, so — when it carries a session — it routes to
  // `onSessionOpen` instead of navigating nowhere.
  isNativePage = null,
  // The PHOTO PICKER's answer. `mediaSamples` is the static form of the same
  // thing; this one is a function so the studio can answer the way the app
  // does — asynchronously, with a genuinely uploaded media handle. Absent →
  // `mediaSamples` (empty by default: a host that cannot pick answers nothing,
  // which is what a role form must survive).
  pickSample = null,
  // THE LONG-PRESS SHEET (`itemOptions`). A long press never deletes on the
  // gesture alone: the cell emits `removeRequested`, the blueprint wires it to
  // `host.itemOptions` and the HOST opens its options sheet, whose Delete tile
  // calls the delete primitive. Absent → the slot is traced only.
  //
  // `crudRemove` is ALIASED to the same opener on purpose. It is the primitive
  // the sheet's Delete calls, never a long-press target — but a blueprint that
  // has not been retargeted yet still points its long press at it, and the
  // studio must show what the PHONE shows (a sheet), not a silent delete.
  onItemOptions = null,
  // WHERE AN APPEND LANDS (`attachItem`). On the phone the host PATCHes the
  // row (`/data/{entry_id} {add_images}`) and refreshes; the studio's rows
  // belong to whoever handed this host its feed, so the append is handed back
  // as `{item_id, images, payload}` for that owner to apply. Absent → the
  // attach is traced and the picker still runs, but nothing lands: a preview
  // with no store of its own has nowhere honest to put the photos.
  onAttach = null,
  // 页面暂存由宿主管理，供原生表单解析本地预览和订阅提交阶段。
  localMediaDraft = null,
} = {}) {
  const log = (op) => (payload) => {
    push?.({ op, payload, at: Date.now() });
    return undefined;
  };
  // A session-opening slot: trace it AND fire the real opener.
  const session = (op) => (payload) => {
    push?.({ op, payload, at: Date.now() });
    onSessionOpen?.(op, payload);
    return undefined;
  };
  // A slot that OPENS THE OPTIONS SHEET: trace it AND ask the host to draw it.
  const options = (op) => (payload) => {
    push?.({ op, payload, at: Date.now() });
    onItemOptions?.(payload || {});
    return undefined;
  };

  // The one piece of state a mock bridge legitimately owns: what the page
  // asked the host CHROME to offer. The real host keeps the same thing (it
  // draws the button); the widget never re-reads it.
  let navAction = null;
  let emit = null;

  return {
    localMediaDraft,
    /** Runtime → bridge: the channel host SIGNALS travel back on. */
    connect: (fn) => {
      emit = fn;
      return () => { if (emit === fn) emit = null; };
    },

    /** Studio 宿主完成 Session 派发后，把真实结果回送给当前蓝图。 */
    triggerCaptureResult: ({ dispatched, reason = null } = {}) => {
      emit?.('captureResult', {
        dispatched: dispatched === true,
        ...(reason ? { reason } : null),
      });
    },

    // navigation — the host owns every page transition
    openItem: (payload) => {
      push?.({ op: 'openItem', payload, at: Date.now() });
      // WHICH page this opens is the CALLER's answer, resolved off the
      // blueprint's own bindings (the page whose modules read `focusEntry`).
      // It used to be the literal string `data_view` — a WebView page id that
      // native has no concept of — so the rename to `detail` would have left
      // every tap on a card navigating nowhere, silently.
      //
      // THE WHOLE PAYLOAD is the page's `page_args`, not just the id: the phone
      // forwards every other key the same way (`openWidgetRuntimeItemView`'s
      // `pageArgs`), which is how a home card preselects a view — `{item_id,
      // tab}`. Reading only `item_id` here truncated that, so the studio's
      // detail page always opened on its default tab while the phone's did not.
      if (itemPage) navigatePage?.(itemPage, { ...(payload || {}), item_id: payload?.item_id });
    },
    openPage: (payload) => {
      push?.({ op: 'openPage', payload, at: Date.now() });
      // The target page file, in all three spellings the fleet writes it:
      // `page_file` (art-studio's connections), `file` (my-wardrobe's board and
      // every scene-studio template option) and the bare `page`. Reading only
      // the first two meant a template tap in the live preview navigated
      // nowhere at all.
      const guess = payload?.page_file || payload?.file || payload?.page || null;
      const pageId = guess ? String(guess).replace(/\.html$/, '') : null;
      // A page THIS bundle renders natively → navigate. Anything else is a
      // DELEGATED WebView page (scene-studio's `template_detail.html`) the
      // native studio cannot run; when it carries a session (its resolved
      // payload holds the template's locked prompt) hand it to the opener,
      // which reproduces the capture that delegated flow culminates in — rather
      // than navigating nowhere, which is all it did before.
      if (pageId && isNativePage?.(pageId)) {
        navigatePage?.(pageId, payload?.args || payload);
        return undefined;
      }
      onSessionOpen?.('openPage', payload);
      return undefined;
    },
    closePage: (payload) => {
      push?.({ op: 'closePage', payload, at: Date.now() });
      // A PAGE MAY CLOSE WITH A RESULT. `close_page {action: "capture", images…}`
      // is an edit page handing back the work it just finished on its way out —
      // doc-digitizer's ScanEditor Confirm, which is where a deferred capture
      // (`open_capture {defer_dispatch, target_page}`) finally becomes a
      // session: one document, one session, created at Confirm and not at the
      // shutter. A plain close carries no action and is only a way back.
      if (payload?.action) onSessionOpen?.('closePage', payload);
      navigatePage?.('index', {});
    },
    openSession: session('openSession'),
    openMainSession: session('openMainSession'),
    openCapture: session('openCapture'),
    logData: log('logData'),
    crudRemove: options('crudRemove'),
    itemOptions: options('itemOptions'),
    imageTap: log('imageTap'),

    /**
     * THE APPEND LANE — photos onto a row that already exists (the phone's
     * `attach_item_image`: picker → upload → PATCH `/data/{entry_id}
     * {add_images}` → refresh). The host owns every step, which is exactly why
     * a widget must never rebuild it out of `pickMedia` + a chained
     * `host.hostResult` — and a BARE `hostResult` wire matches every answering
     * op, so that rebuild also carried a hazard.
     *
     * Two halves, and `dispatch` is the seam between them: the append itself
     * (always), then CONTINUING the row's source session so the agent re-reads
     * it (only when `dispatch` is not false). `dispatch:false` stops at the
     * append — nothing runs, which is what a "+ add page" means, and a preview
     * that started a session anyway would show the opposite of what the
     * blueprint asked for.
     *
     * It ANSWERS NOTHING, like the real host: a returned value would fire
     * `host.hostResult`, re-introducing the hazard this slot exists to retire.
     */
    attachItem: (payload) => {
      push?.({ op: 'attachItem', payload, at: Date.now() });
      const max = Number(payload?.max) || 1;
      Promise.resolve(pickSample ? pickSample(max, payload) : mediaSamples.slice(0, max))
        .then((v) => (Array.isArray(v) ? v.slice(0, max) : (v ? [v] : [])))
        .then((images) => {
          // A cancelled picker attaches nothing and starts nothing — the phone
          // user backing out of the camera, which every lane must survive.
          if (!images.length) return;
          onAttach?.({ item_id: payload?.item_id ?? payload?.entry_id ?? null, images, payload });
          if (payload?.dispatch !== false) onSessionOpen?.('attachItem', { ...payload, images });
        })
        .catch(() => { /* a failed pick is the host's to report */ });
      return undefined;
    },

    // ── the shell slots (prod's reload_widget_data / widget_subview /
    // session_badge_target) ───────────────────────────────────────────────
    // Each is host CHROME talking, not data: the studio has no chrome to move,
    // so recording the request is the whole of the mock's job — an author still
    // sees in the trace that the page asked for it.
    reloadData: log('reloadData'),
    setSubview: log('setSubview'),
    setSessionBadge: log('setSessionBadge'),

    // ── the generic capability slots ──────────────────────────────────────
    // Each is a real device capability the app owns and the widget must never
    // hand-roll: saving a file, leaving the app, writing the clipboard.
    download: log('download'),
    openUrl: log('openUrl'),
    copyText: (payload) => {
      push?.({ op: 'copyText', payload, at: Date.now() });
      // Best-effort so a studio copy button actually copies; a denied or
      // absent clipboard is not an error the widget should ever see.
      try { navigator?.clipboard?.writeText?.(String(payload?.text ?? '')); } catch { /* no clipboard here */ }
      return undefined;
    },

    // ── the nav-action channel (out: declare · in: triggered) ─────────────
    declareNavAction: (payload) => {
      navAction = payload?.action ?? null;
      push?.({ op: 'declareNavAction', payload, at: Date.now() });
      onNavAction?.(navAction);
      return undefined;
    },
    /** What the page last asked the host chrome to offer (studio chrome reads it). */
    declaredNavAction: () => navAction,
    /** The host chrome's button being tapped → back into the blueprint. */
    triggerNavAction: (action) => {
      const which = action ?? navAction;
      if (!which || !emit) return false;
      push?.({ op: 'navActionTriggered', payload: { action: which }, at: Date.now() });
      emit('navActionTriggered', { action: which });
      return true;
    },

    // The one host op that ANSWERS: the real bridge resolves with the picked
    // media, which a blueprint chains into a background capture — or, for a
    // role form, into the well that asked. A PROMISE is a valid answer: the
    // interpreter already resolves one before emitting `host.hostResult`, and
    // a real picker is never synchronous.
    pickMedia: (payload) => {
      push?.({ op: 'pickMedia', payload, at: Date.now() });
      const max = Number(payload?.max) || 1;
      if (!pickSample) return mediaSamples.slice(0, max);
      return Promise.resolve(pickSample(max, payload))
        .then((v) => (Array.isArray(v) ? v.slice(0, max) : (v ? [v] : [])))
        .catch(() => []);
    },
  };
}

export default createMockHost;
